import React from 'react';
import {
  Typography, List, ListItem, ListItemText, CircularProgress, IconButton, Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowLeft } from 'lucide-react';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 1),
  },
  list: {
    flex: 1,
    overflowY: 'auto',
  },
  listItem: {
    borderRadius: 8,
    marginBottom: 2,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  activeItem: {
    backgroundColor: theme.palette.action.selected,
  },
  noHistory: {
    padding: theme.spacing(4, 2),
    textAlign: 'center',
  },
  timestamp: {
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
}));

interface ConversationSummary {
  id: string;
  title: string | null;
  messageCount: number;
  lastMessageAt: string;
  createdAt: string;
}

interface ConversationListProps {
  conversations: ConversationSummary[];
  isLoading: boolean;
  activeConversationId: string | null;
  onSelect: (conversationId: string) => void;
  onBack: () => void;
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  isLoading,
  activeConversationId,
  onSelect,
  onBack,
}) => {
  const classes = useStyles();

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <IconButton size="small" onClick={onBack}>
          <ArrowLeft size={18} />
        </IconButton>
        <Typography variant="subtitle2">Conversation History</Typography>
      </div>
      <Divider />
      <div className={classes.list}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <CircularProgress size={24} />
          </div>
        ) : conversations.length === 0 ? (
          <div className={classes.noHistory}>
            <Typography variant="body2" color="textSecondary">
              No past conversations yet.
            </Typography>
          </div>
        ) : (
          <List dense disablePadding>
            {conversations.map((conv) => (
              <ListItem
                key={conv.id}
                button
                onClick={() => onSelect(conv.id)}
                className={`${classes.listItem} ${conv.id === activeConversationId ? classes.activeItem : ''}`}
              >
                <ListItemText
                  primary={conv.title || 'Untitled conversation'}
                  secondary={
                    <span className={classes.timestamp}>
                      {conv.messageCount} messages · {formatRelativeTime(conv.lastMessageAt)}
                    </span>
                  }
                  primaryTypographyProps={{ variant: 'body2', noWrap: true }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </div>
    </div>
  );
};
